import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Avatar, Chip, Grid, Button,
  Divider, CircularProgress, TextField, MenuItem, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
} from '@mui/material';
import {
  ArrowBack, Save, Person, Phone, Email, LocationOn, CalendarMonth, PhotoCamera,
  School, CheckCircle, Delete, AttachMoney, BeachAccess, CreditCard, EmojiEvents, UploadFile, Description, InsertDriveFile, Badge, WorkspacePremium, Receipt,
} from '@mui/icons-material';
import { InvokeApi, apiList, postImg } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';
import AsyncStorage from '../../services/AsyncStorage';

const TABS = [
  { id: 'Personal', label: 'Personal Details' },
  { id: 'Attendance', label: 'Attendance' },
  { id: 'Result', label: 'Results' },
  { id: 'Fees', label: 'Fees' },
  { id: 'Leaves', label: 'Leaves' },
  { id: 'Activities', label: 'Activities' },
  { id: 'EscortCards', label: 'Escort Cards' },
  { id: 'Documents', label: 'Documents' },
  { id: 'Edit', label: 'Edit' },
];

const InfoRow = ({ label, value }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography fontSize={13} fontWeight={600} color={value ? '#1a1a1a' : 'text.disabled'}>{value || '—'}</Typography>
  </Box>
);

const STATUS_STYLE = {
  Approved: { bg: '#e8f5e9', color: '#2e7d32' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
  Pending: { bg: '#fff3e0', color: '#e65100' },
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Personal');
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [attendance, setAttendance] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [attYear, setAttYear] = useState(dayjs().year().toString());
  const [fees, setFees] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leavesStatus, setLeavesStatus] = useState('Pending');
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [pendingImageName, setPendingImageName] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultDetail, setResultDetail] = useState([]);
  const [resultDetailLoading, setResultDetailLoading] = useState(false);
  const [escortCards, setEscortCards] = useState([]);
  const [escortLoading, setEscortLoading] = useState(false);
  const [escortDialog, setEscortDialog] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [escortSaving, setEscortSaving] = useState(false);
  const [escortForm, setEscortForm] = useState({
    escort_name: '', escort_phone: '', escort_relation: '',
    emergency_contact: '', notes: '',
    pickup_permissions: { regular: true, emergency: false, events: false },
  });
  const [escortPhoto, setEscortPhoto] = useState(null);
  const [escortPhotoPath, setEscortPhotoPath] = useState('');
  const [escortPhotoUploading, setEscortPhotoUploading] = useState(false);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ title: '', file: null });
  const [docFileName, setDocFileName] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);

  // ── Certificate dialog state ────────────────────────────────────────────────
  const [certDialog, setCertDialog] = useState(false);
  const [certType, setCertType] = useState('promotion');
  const [certPromotedTo, setCertPromotedTo] = useState('');
  const [certAchievement, setCertAchievement] = useState('');
  const [certEvent, setCertEvent] = useState('');
  const [certLoading, setCertLoading] = useState(false);

  const openCertificate = async () => {
    setCertLoading(true);
    const token = await AsyncStorage.getItem('token');
    const params = new URLSearchParams({ type: certType });
    if (student?.academic_year) params.append('ac_year', student.academic_year);
    if (certType === 'promotion' && certPromotedTo) params.append('promoted_to', certPromotedTo);
    if (certType === 'achievement' && certAchievement) params.append('achievement', certAchievement);
    if (certType === 'participation' && certEvent) params.append('event', certEvent);
    const url = `${Config.main_url}/generateCertificate/${id}?${params.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Failed to generate certificate'); setCertLoading(false); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
    setCertLoading(false);
    setCertDialog(false);
  };

  const openFeesReceipts = async () => {
    const token = await AsyncStorage.getItem('token');
    const url = `${Config.main_url}/generateFeesReceiptByStudent/${id}${student?.academic_year ? '?ac_year=' + student.academic_year : ''}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('No fees receipts found'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
  };

  // ── Generate ID card PDF ──────────────────────────────────────────────────
  const openIdCard = async () => {
    const token = await AsyncStorage.getItem('token');
    const url = `${Config.main_url}/generateIdCard/${id}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Failed to generate ID card'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
  };

  const openAppReport = async () => {
    const token = await AsyncStorage.getItem('token');
    const url = `${Config.main_url}/generateApplicationReport/${id}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Failed to generate Application Report'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
  };

  const loadStudent = useCallback(async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStudent, { id: id, });
    const s = data?.student || data?.students?.[0];
    if (s) {
      setStudent(s);
      setForm({
        studentId: s.ID,
        full_name: s.full_name || '',
        phone: s.phone || '',
        pass_plane: s.pass_plane || '',
        roll_number: s.roll_number || '',
        email: s.email || '',
        gender: s.gender || 'Male',
        standard: s.standard || '',
        section: s.section || '',
        academic_year: s.academic_year || '',
        father_name: s.father_name || '',
        mother_name: s.mother_name || '',
        guardian_name: s.guardian_name || '',
        father_phone: s.father_phone || '',
        mother_phone: s.mother_phone || '',
        dob: s.dob ? s.dob.split('T')[0] : '',
        blood_group: s.blood_group || '',
        aadhar: s.aadhar || '',
        whatsapp: s.whatsapp || '',
        current_address: s.current_address || '',
        permanent_address: s.permanent_address || '',
        remarks: s.remarks || '',
        profile_pic_path: s.profile_pic_path || '',
      });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    const init = async () => {
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setSections(secData?.sections || []);
      setAcademicYears(acData?.academic_years || []);
    };
    init();
    loadStudent();
  }, [loadStudent]);

  useEffect(() => {
    if (activeTab !== 'Attendance') return;
    const load = async () => {
      setAttLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getAttendance, {
        studentId: id,
        year: parseInt(attYear),
      });
      setAttendance(data?.attendance || []);
      setAttLoading(false);
    };
    load();
  }, [activeTab, id, attYear]);

  useEffect(() => {
    if (activeTab !== 'Fees') return;
    const load = async () => {
      setFeesLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getFeesBalance, { stdId: id });
      setFees(data?.fees || []);
      setFeesLoading(false);
    };
    load();
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'Leaves') return;
    const load = async () => {
      setLeavesLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getLeavesStudent, { id: id, status: leavesStatus });
      setLeaves(data?.leaves || []);
      setLeavesLoading(false);
    };
    load();
  }, [activeTab, id, leavesStatus]);

  useEffect(() => {
    if (activeTab !== 'Activities') return;
    const load = async () => {
      setActivitiesLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getKidsActivity, { id });
      setActivities(data?.activities || []);
      setActivitiesLoading(false);
    };
    load();
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'Result' || !student) return;
    const load = async () => {
      setResultsLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getResultByStdSecAcYear, {
        acYear: student.academic_year,
        standard: student.standard,
        section: student.section,
      });
      const all = data?.result || [];
      setResults(all.filter(r => String(r.student_id) === String(id)));
      setResultsLoading(false);
    };
    load();
  }, [activeTab, id, student]);

  useEffect(() => {
    if (activeTab !== 'EscortCards' || !student) return;
    loadEscortCards();
  }, [activeTab, id, student]);

  const loadEscortCards = async () => {
    setEscortLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getEscortCards, {
      student_id: id,
      academic_year: student?.academic_year,
      is_active: 1,
    });
    setEscortCards(data?.escort_cards || []);
    setEscortLoading(false);
  };

  useEffect(() => {
    if (activeTab !== 'Documents') return;
    const load = async () => {
      setDocsLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getStudentDocuments, { student_id: id });
      setDocuments(data?.documents || []);
      setDocsLoading(false);
    };
    load();
  }, [activeTab, id]);

  const handleAddDocument = async () => {
    if (!docForm.title.trim()) { alert('Please enter a document title'); return; }
    if (!docForm.file) { alert('Please select a file'); return; }
    setSavingDoc(true);
    try {
      const formData = new FormData();
      formData.append('student_id', id);
      formData.append('title', docForm.title.trim());
      formData.append('document', docForm.file);
      formData.append('uploaded_by', '1');
      formData.append('uploaded_by_name', 'Admin');
      const data = await postImg(Config.main_url + apiList.addStudentDocument, formData);
      if (data?.code === '200') {
        setDocDialog(false);
        setDocForm({ title: '', file: null });
        setDocFileName('');
        const refresh = await InvokeApi(Config.main_url + apiList.getStudentDocuments, { student_id: id });
        setDocuments(refresh?.documents || []);
      } else {
        alert(data?.message || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    setSavingDoc(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteStudentDocument, {
      id: docId,
      deleted_by: '1',
      deleted_by_name: 'Admin',
    });
    if (data?.code === '200') {
      setDocuments(prev => prev.filter(d => d.ID !== docId));
    } else {
      alert(data?.message || 'Delete failed');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      // 1️⃣ Upload to PHP — saves image file + updates profile_pic_path in DB
      const phpForm = new FormData();
      phpForm.append('profile_pic', file);
      const phpData = await postImg(Config.main_url + apiList.addStudentProfileImage, phpForm);

      if (phpData?.code === '200' || phpData?.code === 200) {
        const imageName = phpData.imageName;
        const saveData = await InvokeApi(Config.main_url + apiList.editStudent, {
          ...form,
          profile_pic_path: imageName,
        });
        if (saveData?.code === '200') {
          setForm(p => ({ ...p, profile_pic_path: imageName }));
          setPendingImageName(null);
          loadStudent();
        } else {
          setPendingImageName(imageName);
          alert('Photo uploaded but profile save failed. Try saving again.');
          return;
        }
      } else {
        alert('Upload failed: ' + (phpData?.message || 'Unknown error'));
        return;
      }

      // try {
      //   const pyForm = new FormData();
      //   pyForm.append('stdid', id);
      //   pyForm.append('profile_image', file); 
      //   const pyData = await postImg(Config.mainpy_url + apiList.AddProfileImgStdPy, pyForm);
      //   if (pyData?.code === '200') {
      //     alert('Profile photo updated and face registered successfully!');
      //   } else {
      //     alert('Profile photo updated! (Face registration: ' + (pyData?.message || 'skipped') + ')');
      //   }
      // } catch (_) {
      //   alert('Profile photo updated! (Face server unavailable)');
      // }

    } catch (err) {
      console.error(err);
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.editStudent, form);
    setSaving(false);
    if (data?.code === '200') { loadStudent(); alert('Updated successfully!'); }
    else alert(data?.message || 'Failed to update');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this student?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteStudent, { id: id, status: 'Inactive' });
    if (data?.code === '200') navigate('/students');
    else alert(data?.message || 'Failed');
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this student?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteStudent, { id: id, status: 'Active' });
    if (data?.code === '200') navigate('/students');
    else alert(data?.message || 'Failed');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const sectionsByStd = sections.filter(s => s.standard === form.standard).map(s => s.section);
  const standards = [...new Set(sections.map(s => s.standard).filter(Boolean))].sort();

  const openResultDetail = async (r) => {
    setSelectedResult(r);
    setResultDetail([]);
    setResultDetailLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getResults, { result_id: r.ID });
    setResultDetail(data?.results || []);
    setResultDetailLoading(false);
  };

  const openEscortDialog = (card = null) => {
    setEditingCard(card);
    setEscortPhoto(null);
    setEscortPhotoPath(card?.escort_photo || '');
    setEscortForm(card ? {
      escort_name: card.escort_name || '',
      escort_phone: card.escort_phone || '',
      escort_relation: card.escort_relation || '',
      emergency_contact: card.emergency_contact || '',
      notes: card.notes || '',
      pickup_permissions: card.pickup_permissions || { regular: true, emergency: false, events: false },
    } : {
      escort_name: '', escort_phone: '', escort_relation: '',
      emergency_contact: '', notes: '',
      pickup_permissions: { regular: true, emergency: false, events: false },
    });
    setEscortDialog(true);
  };

  const handleEscortPhotoUpload = async (file) => {
    setEscortPhotoUploading(true);
    const fd = new FormData();
    fd.append('escort_photo', file);
    const res = await postImg(Config.main_url + apiList.addEscortPhoto, fd);
    if (res?.code === '200') setEscortPhotoPath(res.imageName || '');
    setEscortPhotoUploading(false);
  };

  const handleEscortSave = async () => {
    if (!escortForm.escort_name || !escortForm.escort_phone || !escortForm.escort_relation) {
      alert('Name, Phone and Relation are required'); return;
    }
    if (!/^\d{10}$/.test(escortForm.escort_phone)) {
      alert('Phone must be exactly 10 digits'); return;
    }
    setEscortSaving(true);
    const payload = {
      student_id: id,
      escort_name: escortForm.escort_name,
      escort_phone: escortForm.escort_phone,
      escort_relation: escortForm.escort_relation,
      emergency_contact: escortForm.emergency_contact,
      notes: escortForm.notes,
      escort_photo: escortPhotoPath || '',
      pickup_permissions: escortForm.pickup_permissions,
      academic_year: student?.academic_year,
      valid_from: new Date().toISOString().split('T')[0],
      created_by: '1',
    };
    if (editingCard) payload.id = editingCard.id;
    const url = editingCard ? apiList.updateEscortCard : apiList.addEscortCard;
    const data = await InvokeApi(Config.main_url + url, payload);
    setEscortSaving(false);
    if (data?.code === '200') { setEscortDialog(false); loadEscortCards(); }
    else alert(data?.message || 'Failed');
  };

  const handleEscortDelete = async (cardId) => {
    if (!window.confirm('Delete this escort card?')) return;
    await InvokeApi(Config.main_url + apiList.deleteEscortCard, {
      id: cardId, academic_year: student?.academic_year,
    });
    loadEscortCards();
  };

  const ef = (k) => (e) => setEscortForm(p => ({ ...p, [k]: e.target.value }));
  const togglePerm = (k) => setEscortForm(p => ({
    ...p, pickup_permissions: { ...p.pickup_permissions, [k]: !p.pickup_permissions[k] },
  }));

  const statusColor = (s) => {
    if (s === 'Present') return { bg: '#e8f5e9', color: '#2e7d32' };
    if (s === 'Absent') return { bg: '#ffebee', color: '#c62828' };
    if (s === 'Leave') return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#f5f5f5', color: '#757575' };
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
      <CircularProgress sx={{ color: '#1a237e' }} />
    </Box>
  );

  if (!student) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography color="text.secondary">Student not found</Typography>
      <Button onClick={() => navigate('/students')} sx={{ mt: 2 }}>Back</Button>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/students')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{student.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">ID: #{student.ID} · {student.standard} - {student.section}</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>
            <Box sx={{ height: 70, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
            <CardContent sx={{ pt: 0, textAlign: 'center', pb: 3 }}>
              {/* <Avatar src={student.profile_pic} sx={{
                width: 80, height: 80, border: '3px solid #fff',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                fontSize: 28, fontWeight: 900, color: '#fff', mt: -5, alignItems: 'center'
              }}>
                {student.full_name?.[0]}
              </Avatar> */}
              <Box sx={{ position: 'relative', display: 'inline-block', mt: -5 }}>
                <Avatar src={previewUrl || student.profile_pic} sx={{
                  width: 80, height: 80, border: '3px solid #fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                  fontSize: 28, fontWeight: 900, color: '#fff',
                }}>
                  {student.full_name?.[0]}
                </Avatar>
                <label htmlFor="profile-upload">
                  <input id="profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                  <Tooltip title="Change Photo">
                    <IconButton component="span" size="small" disabled={uploading} sx={{
                      position: 'absolute', bottom: -2, right: -2,
                      background: '#1a237e', color: '#fff', width: 26, height: 26,
                      '&:hover': { background: '#1565c0' },
                    }}>
                      {uploading ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>

              <Typography fontWeight={800} fontSize={15} mt={1.5}>{student.full_name}</Typography>
              <Typography fontSize={12} color="text.secondary">{student.standard} - {student.section}</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${student.ID}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                <Chip label={student.status || 'Active'} size="small"
                  sx={{ background: student.status === 'Active' ? '#e8f5e9' : '#ffebee', color: student.status === 'Active' ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 10 }} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'left', px: 1 }}>
                {[
                  { icon: <Phone fontSize="small" />, value: student.phone },
                  { icon: <Email fontSize="small" />, value: student.email },
                  { icon: <School fontSize="small" />, value: `${student.standard} - ${student.section}` },
                  { icon: <CalendarMonth fontSize="small" />, value: student.academic_year },
                  { icon: <LocationOn fontSize="small" />, value: student.current_address },
                ].map((r, i) => r.value ? (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Box sx={{ color: '#1a237e', flexShrink: 0 }}>{r.icon}</Box>
                    <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button fullWidth variant="contained" startIcon={<Badge />} onClick={openIdCard}
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600, mb: 1 }}>
                Download ID Card
              </Button>
              <Button fullWidth variant="outlined" startIcon={<Badge />} onClick={openAppReport}
                sx={{ borderRadius: 2, color: 'linear-gradient(135deg,#1a237e,#1565c0)', borderColor: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#f3e5f5' } }}>
                Application Report
              </Button>
              <Button fullWidth variant="outlined" startIcon={<WorkspacePremium />} onClick={() => setCertDialog(true)}
                sx={{ borderRadius: 2, color: '#6a1b9a', borderColor: '#6a1b9a', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#f3e5f5' } }}>
                Certificate
              </Button>
              <Button fullWidth variant="outlined" startIcon={<Receipt />} onClick={openFeesReceipts}
                sx={{ borderRadius: 2, color: '#2e7d32', borderColor: '#2e7d32', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#e8f5e9' } }}>
                Fees Receipts
              </Button>
              <Button fullWidth variant="outlined" startIcon={student.status == 'Inactive' ?  '' : <Delete />} onClick={student.status == 'Inactive' ?  handleApprove : handleDelete}
                sx={{ borderRadius: 2, color: student.status == 'Inactive' ? '#2e7d32' : '#c62828', borderColor: student.status == 'Inactive' ? '#2e7d32' : '#c62828', fontSize: 12, fontWeight: 600, '&:hover': { background: '#ffebee' } }}>
                {student.status == 'Inactive' ? 'Restore' : 'Delete' } Student
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <Box key={t.id} onClick={() => setActiveTab(t.id)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, px: 2, py: 1,
                borderRadius: 10, cursor: 'pointer', border: '1.5px solid',
                borderColor: activeTab === t.id ? '#1a237e' : '#e8eaf6',
                background: activeTab === t.id ? '#e8eaf6' : '#fff',
                transition: 'all 0.2s',
              }}>
                <Typography fontSize={13} fontWeight={700} color={activeTab === t.id ? '#1a237e' : 'text.secondary'}>{t.label}</Typography>
              </Box>
            ))}
          </Box>

          {activeTab === 'Personal' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Student Information</Typography>
                <Grid container spacing={0}>
                  {[
                    { label: 'Full Name', value: student.full_name },
                    { label: 'Phone', value: student.phone },
                    { label: 'Password', value: student.pass_plane },
                    { label: 'Email', value: student.email },
                    { label: 'Gender', value: student.gender },
                    { label: 'Date of Birth', value: student.dob ? dayjs(student.dob).format('DD MMM YYYY') : null },
                    { label: 'Blood Group', value: student.blood_group },
                    { label: 'Roll Number', value: student.roll_number },
                    { label: 'Standard', value: student.standard },
                    { label: 'Section', value: student.section },
                    { label: 'Academic Year', value: student.academic_year },
                    { label: 'Father Name', value: student.father_name },
                    { label: 'Father Phone', value: student.father_phone },
                    { label: 'Mother Name', value: student.mother_name },
                    { label: 'Mother Phone', value: student.mother_phone },
                    { label: 'Aadhar', value: student.aadhar },
                    { label: 'Current Address', value: student.current_address },
                    { label: 'Permanent Address', value: student.permanent_address },
                    { label: 'Remarks', value: student.remarks },
                    { label: 'Status', value: student.status },
                  ].map(({ label, value }) => (
                    <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                      <InfoRow label={label} value={value} />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {activeTab === 'Documents' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Documents</Typography>
                  <Button variant="contained" startIcon={<UploadFile />} onClick={() => setDocDialog(true)}
                    sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                    Add Document
                  </Button>
                </Box>

                {docsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} size={32} />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Description sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary" fontWeight={500}>No documents uploaded yet</Typography>
                    <Button variant="contained" startIcon={<UploadFile />} onClick={() => setDocDialog(true)}
                      sx={{ mt: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                      Upload First Document
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {documents.map((doc) => {
                      const isImage = doc.file_type?.startsWith('image/');
                      const isPdf = doc.file_type === 'application/pdf';
                      const fileUrl = Config.main_url + '/' + doc.file_path;
                      return (
                        <Box key={doc.ID} sx={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          p: 2, borderRadius: 2,
                          border: '1px solid #e8eaf6',
                          borderLeft: '4px solid #1a237e',
                          background: '#fff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                          <Box sx={{
                            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                            background: isPdf ? '#ffebee' : isImage ? '#e8f5e9' : '#e8eaf6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <InsertDriveFile sx={{
                              fontSize: 24,
                              color: isPdf ? '#c62828' : isImage ? '#2e7d32' : '#1a237e',
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={700} fontSize={14} color="#1a237e" noWrap>
                              {doc.title}
                            </Typography>
                            <Typography fontSize={11} color="text.secondary" noWrap>
                              {doc.file_name}
                              {/* {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(1)} KB` : ''} */}
                            </Typography>
                            <Typography fontSize={11} color="text.disabled">
                              {doc.uploaded_by_name || 'Admin'} · {doc.created_at ? dayjs(doc.created_at).format('DD MMM YYYY') : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title="View / Download">
                              <IconButton size="small" onClick={() => window.open(fileUrl, '_blank')}
                                sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
                                <Description fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Document">
                              <IconButton size="small" onClick={() => handleDeleteDocument(doc.ID)}
                                sx={{ color: '#c62828', border: '1px solid #ffebee', borderRadius: 1.5 }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Attendance' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Attendance Record</Typography>
                  <TextField select size="small" label="Year" value={attYear}
                    onChange={e => setAttYear(e.target.value)} sx={{ minWidth: 100 }}>
                    {[dayjs().year(), dayjs().year() - 1, dayjs().year() - 2].map(y => (
                      <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {attLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : attendance.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircle sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No attendance data for {attYear}</Typography>
                  </Box>
                ) : (
                  <Box>
                    {attendance.map((row, idx) => {
                      const a = row.attendance?.[0] || {};
                      const present = Number(a.present) ?? 0;
                      const absent = Number(a.absent) ?? 0;
                      const leave = Number(a.leave) ?? 0;
                      const total = present + absent + leave;
                      const pct = total > 0 ? Math.round((present / total) * 100) : 0;
                      return (
                        <Box key={row.id || idx} sx={{
                          mb: 1.5, p: 2, borderRadius: 2,
                          border: '1px solid #e8eaf6',
                          borderLeft: '4px solid #1a237e',
                          background: '#fafafa',
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography fontWeight={700} fontSize={14} color="#1a237e">{row.month}</Typography>
                            <Typography fontSize={12} fontWeight={700} color="#1a237e">{pct}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={pct} sx={{
                            height: 5, borderRadius: 3, mb: 1, background: '#e8eaf6',
                            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#1a237e,#1565c0)', borderRadius: 3 },
                          }} />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: `Present: ${present}`, bg: '#e8f5e9', color: '#2e7d32' },
                              { label: `Absent: ${absent}`, bg: '#ffebee', color: '#c62828' },
                              { label: `Leave: ${leave}`, bg: '#fff3e0', color: '#e65100' },
                              ...(a.late ? [{ label: `Late: ${a.late}`, bg: '#f3e5f5', color: '#6a1b9a' }] : []),
                              ...(a.week_off ? [{ label: `Week Off: ${a.week_off}`, bg: '#e0f2f1', color: '#00695c' }] : []),
                            ].map(s => (
                              <Chip key={s.label} label={s.label} size="small"
                                sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: 11, height: 22 }} />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Result' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Academic Results</Typography>
                {resultsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : results.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <EmojiEvents sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No results found</Typography>
                  </Box>
                ) : (
                  results.map((r, i) => {
                    const pct = parseFloat(r.percentage || 0);
                    const grade = pct >= 75 ? { bg: '#e8f5e9', color: '#2e7d32', label: 'A' }
                      : pct >= 45 ? { bg: '#fff3e0', color: '#e65100', label: 'B' }
                        : { bg: '#ffebee', color: '#c62828', label: 'F' };
                    return (
                      <Box key={r.ID || i}
                        onClick={() => openResultDetail(r)}
                        sx={{
                          mb: 1.5, p: 2, borderRadius: 2, cursor: 'pointer',
                          border: '1px solid #e8eaf6',
                          borderLeft: '4px solid #1a237e',
                          background: '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': { background: '#e8eaf6', borderColor: '#1a237e' },
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography fontWeight={700} fontSize={14} color="#1a237e">{r.exam_type}</Typography>
                            <Typography fontSize={12} color="text.secondary">{r.ac_year}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={`${pct}%`} size="small" sx={{ background: grade.bg, color: grade.color, fontWeight: 800, fontSize: 13 }} />
                            {r.rank && <Chip label={`Rank ${r.rank}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />}
                          </Box>
                        </Box>
                        {r.remarks && <Typography fontSize={12} color="text.secondary" mt={0.5}>{r.remarks}</Typography>}
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          )}

          <Dialog open={!!selectedResult} onClose={() => setSelectedResult(null)} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
              Result Details — {selectedResult?.exam_type}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedResult && (() => {
                const pct = parseFloat(selectedResult.percentage || 0);
                const grade = pct >= 75 ? { bg: '#e8f5e9', color: '#2e7d32', label: 'A' }
                  : pct >= 45 ? { bg: '#fff3e0', color: '#e65100', label: 'B' }
                    : { bg: '#ffebee', color: '#c62828', label: 'F' };
                return (
                  <Box>
                    <Box sx={{
                      p: 3, borderRadius: 3, mb: 3, textAlign: 'center',
                      background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    }}>
                      <Typography fontSize={52} fontWeight={900} color="white" lineHeight={1}>{pct}%</Typography>
                      <Typography fontSize={14} color="rgba(255,255,255,0.8)" mt={0.5}>Overall Percentage</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                        <Chip label={selectedResult.exam_type} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                        {selectedResult.rank && <Chip label={`Rank: ${selectedResult.rank}`} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />}
                        <Chip label={selectedResult.ac_year} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                        <Chip label={`Grade ${grade.label}`} sx={{ background: grade.bg, color: grade.color, fontWeight: 800 }} />
                      </Box>
                      {selectedResult.remarks && (
                        <Typography fontSize={13} color="rgba(255,255,255,0.75)" mt={1}>{selectedResult.remarks}</Typography>
                      )}
                    </Box>

                    <Typography fontWeight={700} color="#1a237e" mb={1.5}>Subject-wise Marks</Typography>
                    {resultDetailLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress sx={{ color: '#1a237e' }} size={28} />
                      </Box>
                    ) : resultDetail.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography color="text.secondary" fontSize={13}>No subject-wise data available</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 480 }}>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                            background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                            borderRadius: '8px 8px 0 0', px: 2, py: 1.2,
                          }}>
                            {['Subject', 'Max', 'Min', 'Obtained', 'Internals', 'Grade'].map(h => (
                              <Typography key={h} fontSize={12} fontWeight={700} color="white" textAlign="center"
                                sx={{ '&:first-of-type': { textAlign: 'left' } }}>
                                {h}
                              </Typography>
                            ))}
                          </Box>
                          {resultDetail.map((item, idx) => {
                            const obt = parseFloat(item.obt_marks || 0);
                            const max = parseFloat(item.max_marks || 0);
                            const subPct = max > 0 ? Math.round((obt / max) * 100) : 0;
                            const pass = item.status === 'Pass' || obt >= parseFloat(item.min_marks || 0);
                            return (
                              <Box key={idx} sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                                px: 2, py: 1.2,
                                background: idx % 2 === 0 ? '#fafafa' : 'white',
                                borderBottom: '1px solid #f0f0f0',
                                borderRadius: idx === resultDetail.length - 1 ? '0 0 8px 8px' : 0,
                              }}>
                                <Typography fontSize={13} fontWeight={600} color="#1a237e">{item.subject}</Typography>
                                <Typography fontSize={13} textAlign="center" color="text.secondary">{item.max_marks || '—'}</Typography>
                                <Typography fontSize={13} textAlign="center" color="text.secondary">{item.min_marks || '—'}</Typography>
                                <Typography fontSize={13} textAlign="center" fontWeight={700}
                                  color={pass ? '#2e7d32' : '#c62828'}>
                                  {item.obt_marks || '—'}
                                </Typography>
                                <Typography fontSize={13} textAlign="center" color="text.secondary">{item.internals || '—'}</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Chip
                                    label={item.status || (pass ? 'Pass' : 'Fail')}
                                    size="small"
                                    sx={{
                                      background: pass ? '#e8f5e9' : '#ffebee',
                                      color: pass ? '#2e7d32' : '#c62828',
                                      fontWeight: 700, fontSize: 10, height: 20,
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={() => setSelectedResult(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
            </DialogActions>
          </Dialog>

          {activeTab === 'Fees' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Fee Summary</Typography>
                {feesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : !fees || Object.keys(fees).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AttachMoney sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No fee records found</Typography>
                  </Box>
                ) : (() => {
                  const total = parseFloat(fees.totalFees || 0);
                  const paid = parseFloat(fees.totalFeesPaid || 0);
                  const balance = parseFloat(fees.totalFeesBalance || 0);
                  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
                  const isPaid = balance <= 0;
                  return (
                    <Box>
                      <Box sx={{
                        p: 2.5, borderRadius: 3, mb: 3, textAlign: 'center',
                        background: isPaid ? 'linear-gradient(135deg,#2e7d32,#388e3c)' : 'linear-gradient(135deg,#c62828,#d32f2f)',
                      }}>
                        <Typography fontSize={13} sx={{ color: 'rgba(255,255,255,0.8)' }}>Status</Typography>
                        <Typography fontSize={22} fontWeight={900} color="white">{isPaid ? '✓ Fully Paid' : `₹${balance.toLocaleString()} Pending`}</Typography>
                        <Typography fontSize={12} sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{student?.academic_year}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography fontSize={12} color="text.secondary">Payment Progress</Typography>
                          <Typography fontSize={12} fontWeight={700} color="#1a237e">{pct}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{
                          height: 8, borderRadius: 4, background: '#ffebee',
                          '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#2e7d32,#4caf50)', borderRadius: 4 },
                        }} />
                      </Box>
                      <Grid container spacing={1.5}>
                        {[
                          { label: 'Total Fees', value: `₹${total.toLocaleString()}`, bg: '#e8eaf6', color: '#1a237e' },
                          { label: 'Paid', value: `₹${paid.toLocaleString()}`, bg: '#e8f5e9', color: '#2e7d32' },
                          { label: 'Balance', value: `₹${balance.toLocaleString()}`, bg: balance > 0 ? '#ffebee' : '#e8f5e9', color: balance > 0 ? '#c62828' : '#2e7d32' },
                        ].map(s => (
                          <Grid item xs={4} key={s.label}>
                            <Box sx={{ p: 1.5, borderRadius: 2, background: s.bg, textAlign: 'center' }}>
                              <Typography fontSize={11} color="text.secondary">{s.label}</Typography>
                              <Typography fontSize={14} fontWeight={800} color={s.color}>{s.value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Leaves' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Leave Applications</Typography>
                  <TextField select size="small" label="Status" value={leavesStatus} onChange={e => setLeavesStatus(e.target.value)} sx={{ minWidth: 130 }}>
                    {['Pending', 'Approved', 'Rejected'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Box>
                {leavesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : leaves.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <BeachAccess sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No {leavesStatus.toLowerCase()} leaves</Typography>
                  </Box>
                ) : (
                  leaves.map((l, i) => {
                    const s = STATUS_STYLE[l.status] || STATUS_STYLE.Pending;
                    return (
                      <Box key={i}>
                        <Box sx={{ py: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip label={l.leave_type || 'Leave'} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 11 }} />
                              <Chip label={l.status} size="small" sx={{ background: s.bg, color: s.color, fontWeight: 600, fontSize: 11 }} />
                            </Box>
                            <Typography fontSize={12} color="text.secondary">
                              {l.start_date ? dayjs(l.start_date).format('DD MMM YYYY') : '—'} → {l.end_date ? dayjs(l.end_date).format('DD MMM YYYY') : '—'}
                            </Typography>
                            {l.leave_details && <Typography fontSize={12} color="text.secondary" mt={0.3}>{l.leave_details}</Typography>}
                          </Box>
                        </Box>
                        {i < leaves.length - 1 && <Divider />}
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'EscortCards' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Authorized Pickup Persons</Typography>
                  <Button variant="contained" startIcon={<CreditCard />} onClick={() => openEscortDialog()}
                    sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                    Add Escort Card
                  </Button>
                </Box>

                {escortLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : escortCards.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CreditCard sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No escort cards found</Typography>
                    <Typography variant="caption" color="text.secondary">Add authorized persons who can pick up this student</Typography>
                  </Box>
                ) : (
                  escortCards.map((card, i) => (
                    <Box key={card.id || i} sx={{
                      mb: 2, p: 2, borderRadius: 2,
                      border: '1px solid #e8eaf6',
                      borderLeft: '4px solid #1a237e',
                      background: '#fafafa',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                        {card.escort_photo ? (
                          <Box component="img" src={card.escort_photo} alt={card.escort_name}
                            sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e8eaf6' }} />
                        ) : (
                          <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg,#1a237e,#1565c0)', flexShrink: 0, fontSize: 20 }}>
                            {card.escort_name?.[0] || 'E'}
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={700} fontSize={15} color="#1a237e">{card.escort_name}</Typography>
                          <Typography fontSize={13} color="#1565c0" fontWeight={600}>{card.escort_relation}</Typography>
                          <Typography fontSize={13} color="text.secondary">📞 {card.escort_phone}</Typography>
                          {card.emergency_contact && (
                            <Typography fontSize={12} color="text.disabled">Emergency: {card.emergency_contact}</Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEscortDialog(card)} sx={{ color: '#1565c0', background: '#e3f2fd', borderRadius: 1.5 }}>
                              <Save sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleEscortDelete(card.id)} sx={{ color: '#c62828', background: '#ffebee', borderRadius: 1.5 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {card.pickup_permissions && (
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: card.notes ? 1 : 0 }}>
                          <Typography fontSize={12} color="text.secondary" fontWeight={600} mr={0.5}>Permissions:</Typography>
                          {card.pickup_permissions.regular && <Chip label="Regular ✓" size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 10, height: 20 }} />}
                          {card.pickup_permissions.emergency && <Chip label="Emergency ✓" size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: 10, height: 20 }} />}
                          {card.pickup_permissions.events && <Chip label="Events ✓" size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10, height: 20 }} />}
                        </Box>
                      )}

                      {card.notes && (
                        <Box sx={{ mt: 1, p: 1, background: '#f5f5f5', borderRadius: 1 }}>
                          <Typography fontSize={12} color="text.secondary" fontStyle="italic">📝 {card.notes}</Typography>
                        </Box>
                      )}
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          <Dialog open={escortDialog} onClose={() => setEscortDialog(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
              {editingCard ? 'Edit Escort Card' : 'Add Escort Card'}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {(escortPhoto || escortPhotoPath) ? (
                      <Box component="img"
                        src={escortPhoto ? URL.createObjectURL(escortPhoto) : escortPhotoPath}
                        alt="escort"
                        sx={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e8eaf6' }}
                      />
                    ) : (
                      <Avatar sx={{ width: 64, height: 64, background: '#e8eaf6', color: '#1a237e', fontSize: 24 }}>👤</Avatar>
                    )}
                    <Button variant="outlined" component="label" sx={{ borderRadius: 2 }} disabled={escortPhotoUploading}>
                      {escortPhotoUploading ? 'Uploading...' : escortPhoto ? `✓ ${escortPhoto.name}` : 'Upload Photo'}
                      <input type="file" hidden accept="image/*" onChange={e => {
                        const file = e.target.files[0];
                        if (file) { setEscortPhoto(file); handleEscortPhotoUpload(file); }
                      }} />
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Name *" value={escortForm.escort_name} onChange={ef('escort_name')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Phone * (10 digits)" value={escortForm.escort_phone}
                    onChange={e => setEscortForm(p => ({ ...p, escort_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    inputProps={{ maxLength: 10 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Relation *" value={escortForm.escort_relation} onChange={ef('escort_relation')}
                    placeholder="e.g. Father, Mother, Uncle" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Emergency Contact (10 digits)" value={escortForm.emergency_contact}
                    onChange={e => setEscortForm(p => ({ ...p, emergency_contact: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    inputProps={{ maxLength: 10 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Notes" value={escortForm.notes} onChange={ef('notes')} multiline rows={2} />
                </Grid>
                <Grid item xs={12}>
                  <Typography fontSize={13} fontWeight={600} color="#1a237e" mb={1}>Pickup Permissions</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { key: 'regular', label: 'Regular Pickup' },
                      { key: 'emergency', label: 'Emergency Pickup' },
                      { key: 'events', label: 'Event Pickup' },
                    ].map(p => (
                      <Chip key={p.key} label={p.label} clickable onClick={() => togglePerm(p.key)}
                        sx={{
                          fontWeight: 700, fontSize: 12,
                          background: escortForm.pickup_permissions[p.key] ? '#1a237e' : '#e8eaf6',
                          color: escortForm.pickup_permissions[p.key] ? 'white' : '#1a237e',
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={() => setEscortDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
              <Button onClick={handleEscortSave} variant="contained" disabled={escortSaving}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                {escortSaving ? <CircularProgress size={20} color="inherit" /> : (editingCard ? 'Update' : 'Save')}
              </Button>
            </DialogActions>
          </Dialog>

          {activeTab === 'Activities' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Kids Activities</Typography>
                {activitiesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} size={32} />
                  </Box>
                ) : activities.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography fontSize={40} mb={1}>🎨</Typography>
                    <Typography color="text.secondary">No activities found</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add activities from Kids Activities page
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {activities.map((a, i) => (
                      <Grid item xs={12} sm={6} key={a.ID || i}>
                        <Box sx={{ borderRadius: 2, border: '1px solid #e8eaf6', overflow: 'hidden', borderLeft: '4px solid #1a237e' }}>
                          {(a.kids_activity_url || a.kids_activity) && (
                            <Box component="img"
                              src={a.kids_activity_url || a.kids_activity}
                              alt={a.title}
                              sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                            />
                          )}
                          <Box sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography fontWeight={700} fontSize={13}>{a.title}</Typography>
                              {a.type && (
                                <Chip label={a.type} size="small"
                                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} />
                              )}
                            </Box>
                            {a.details && (
                              <Typography fontSize={12} color="text.secondary" sx={{
                                display: '-webkit-box', WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}>
                                {a.details}
                              </Typography>
                            )}
                            <Typography fontSize={11} color="text.disabled" mt={0.5}>
                              {a.created_at ? a.created_at.split('T')[0] : '—'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          )}

          {/* Add Document Dialog */}
          <Dialog open={docDialog} onClose={() => { setDocDialog(false); setDocForm({ title: '', file: null }); setDocFileName(''); }}
            maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
              Add Document
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Document Title *"
                    value={docForm.title}
                    onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Aadhar Card, Offer Letter, Certificate" />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    border: '2px dashed #c5cae9', borderRadius: 2, p: 2.5,
                    textAlign: 'center', cursor: 'pointer',
                    background: docFileName ? '#e8eaf6' : '#fafafa',
                    '&:hover': { borderColor: '#1a237e', background: '#e8eaf6' },
                  }}
                    onClick={() => document.getElementById('doc-file-input').click()}>
                    <input
                      id="doc-file-input"
                      type="file"
                      style={{ display: 'none' }}
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) { setDocForm(p => ({ ...p, file: f })); setDocFileName(f.name); }
                      }}
                    />
                    <UploadFile sx={{ fontSize: 32, color: docFileName ? '#1a237e' : '#9e9e9e', mb: 0.5 }} />
                    <Typography fontSize={13} fontWeight={600} color={docFileName ? '#1a237e' : 'text.secondary'}>
                      {docFileName || 'Click to select file'}
                    </Typography>
                    <Typography fontSize={11} color="text.disabled" mt={0.3}>
                      PDF, Images, Word, Excel, Text · Max 10 MB
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={() => { setDocDialog(false); setDocForm({ title: '', file: null }); setDocFileName(''); }}
                variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
              <Button onClick={handleAddDocument} variant="contained" disabled={savingDoc}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                {savingDoc ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
              </Button>
            </DialogActions>
          </Dialog>

          {activeTab === 'Edit' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Edit Student</Typography>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    onClick={handleSave} disabled={saving}
                    sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Full Name *" value={form.full_name || ''} onChange={f('full_name')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone" value={form.phone || ''} onChange={f('phone')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Password" value={form.pass_plane || ''} onChange={f('pass_plane')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Roll Number" value={form.roll_number || ''} onChange={f('roll_number')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" value={form.email || ''} onChange={f('email')} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Gender" value={form.gender || 'Male'} onChange={f('gender')}>
                      {['Male', 'Female', 'Other'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date of Birth" value={form.dob || ''} onChange={f('dob')} InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select fullWidth size="small" label="Standard" value={form.standard || ''} onChange={e => setForm(p => ({ ...p, standard: e.target.value, section: '' }))}>
                      {standards.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select fullWidth size="small" label="Section" value={form.section || ''} onChange={f('section')}>
                      {sectionsByStd.length > 0 ? sectionsByStd.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>) : <MenuItem value={form.section || ''}>{form.section || '—'}</MenuItem>}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select fullWidth size="small" label="Academic Year" value={form.academic_year || ''} onChange={f('academic_year')}>
                      {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Father Name" value={form.father_name || ''} onChange={f('father_name')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Father Phone" value={form.father_phone || ''} onChange={f('father_phone')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mother Name" value={form.mother_name || ''} onChange={f('mother_name')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mother Phone" value={form.mother_phone || ''} onChange={f('mother_phone')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Guardian Name" value={form.guardian_name || ''} onChange={f('guardian_name')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="WhatsApp" value={form.whatsapp || ''} onChange={f('whatsapp')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Blood Group" value={form.blood_group || ''} onChange={f('blood_group')} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Aadhar" value={form.aadhar || ''} onChange={f('aadhar')} /></Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label="Current Address" value={form.current_address || ''} onChange={f('current_address')} /></Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label="Permanent Address" value={form.permanent_address || ''} onChange={f('permanent_address')} /></Grid>
                  <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" value={form.remarks || ''} onChange={f('remarks')} /></Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* ── Certificate Dialog ── */}
      <Dialog open={certDialog} onClose={() => setCertDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#6a1b9a', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkspacePremium sx={{ color: '#6a1b9a' }} />
            Generate Certificate — {student?.full_name}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField select fullWidth size="small" label="Certificate Type" value={certType}
            onChange={e => setCertType(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="promotion">Promotion Certificate</MenuItem>
            <MenuItem value="achievement">Achievement Certificate</MenuItem>
            <MenuItem value="participation">Participation Certificate</MenuItem>
          </TextField>

          {certType === 'promotion' && (
            <TextField fullWidth size="small" label="Promoted To (e.g. Class 2)" value={certPromotedTo}
              onChange={e => setCertPromotedTo(e.target.value)} sx={{ mb: 2 }}
              placeholder="e.g. Class 2 - Section A" />
          )}
          {certType === 'achievement' && (
            <TextField fullWidth size="small" label="Achievement Description" value={certAchievement}
              onChange={e => setCertAchievement(e.target.value)} sx={{ mb: 2 }}
              placeholder="e.g. First Place in Science Exhibition" />
          )}
          {certType === 'participation' && (
            <TextField fullWidth size="small" label="Event Name" value={certEvent}
              onChange={e => setCertEvent(e.target.value)} sx={{ mb: 2 }}
              placeholder="e.g. Annual Sports Day 2025" />
          )}

          <Box sx={{ p: 2, background: '#f3e5f5', borderRadius: 2, border: '1px solid #ce93d8' }}>
            <Typography fontSize={12} color="#6a1b9a" fontWeight={600}>Preview:</Typography>
            <Typography fontSize={12} color="#555" mt={0.5}>
              {certType === 'promotion' && `Promotion certificate for ${student?.full_name} — promoted to ${certPromotedTo || '...'}`}
              {certType === 'achievement' && `Achievement certificate for ${student?.full_name} — ${certAchievement || '...'}`}
              {certType === 'participation' && `Participation certificate for ${student?.full_name} — ${certEvent || '...'}`}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setCertDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={openCertificate} variant="contained" disabled={certLoading}
            sx={{ background: 'linear-gradient(135deg,#6a1b9a,#8e24aa)', borderRadius: 2, fontWeight: 700 }}>
            {certLoading ? <CircularProgress size={20} color="inherit" /> : 'Generate & Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
